import React, { useState, useEffect } from "react";
import moment from "moment";
import { Line, Bar, HorizontalBar, Doughnut, Chart } from "react-chartjs-2";
import FETCH from "../../../utils/fetch";
import "../BudgetMain.css";
import budgetHomeIcon from "../../../assets/images/savings.png";
import budgetIcon from "../../../assets/images/performance.png";
import DEATH_API from "../../../apis/death.api";
import { ChartColors } from "../../../components/ChartColors";
import { Progress, Skeleton } from "antd";
import { BudgetTransaction } from "./BudgetTransaction";
import BUDGET_API from "../../../apis/budget.api";

const BudgetSummarySkeleton = () => (
  <div className="budget-summary-skeleton" aria-hidden="true">
    <div className="budget-summary-skeleton__left">
      <Skeleton active avatar title={false} paragraph={false} />
    </div>
    <div className="budget-summary-skeleton__right">
      <Skeleton active title={{ width: "42%" }} paragraph={false} />
      <Skeleton active title={{ width: "58%" }} paragraph={false} />
      <Skeleton active title={{ width: "70%" }} paragraph={false} />
      <Skeleton active title={{ width: "36%" }} paragraph={false} />
      <Skeleton active title={{ width: "70%" }} paragraph={false} />
    </div>
  </div>
);

const BudgetListSkeleton = ({ rows = 4 }) => (
  <div className="budget-categories-skeleton" aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="budget-categories-skeleton__row" key={index}>
        <div className="budget-categories-skeleton__icon" />
        <div className="budget-categories-skeleton__content">
          <div className="budget-categories-skeleton__header">
            <div className="budget-categories-skeleton__line budget-categories-skeleton__line--title" />
            <div className="budget-categories-skeleton__line budget-categories-skeleton__line--value" />
          </div>
          <div className="budget-categories-skeleton__progress" />
          <div className="budget-categories-skeleton__line budget-categories-skeleton__line--footer" />
        </div>
      </div>
    ))}
  </div>
);

export const BudgetReport = ({ transactions, clientBanks, isLoading = false }) => {
  const [loading, setLoading] = useState(false);
  const [assetsAmount, setAssetsAmount] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [totalBudget, setTotalBudget] = useState(null);
  const [categoryBudget, setCategoryBudget] = useState(null);
  const [monthsBudgetData, setMonthsBudgetData] = useState([]);

  const getCalculatedBudget = async () => {
    try {
      setLoading(true);
      let data = await DEATH_API.getCalculatedBudget();
      if (data && data.status === 200 && data.budgets) {
        console.log("data", data.budgets);
        const categorySums = {};

        data.budgets &&
          data.budgets.forEach((item) => {
            const { categoryName, usedAmount, leftAmount } = item && item;
            if (categorySums[categoryName]) {
              categorySums[categoryName].usedAmount += usedAmount;
              categorySums[categoryName].leftAmount += leftAmount;
            } else {
              categorySums[categoryName] = {
                usedAmount,
                leftAmount,
              };
            }
          });
        const categoryName = Object.keys(categorySums);
        const usedAmounts =
          categoryName &&
          categoryName.map((category) => categorySums[category].usedAmount);
        const leftAmounts =
          categoryName &&
          categoryName.map((category) => categorySums[category].leftAmount);
        const amountsSums =
          usedAmounts &&
          usedAmounts.map((used, index) => used + leftAmounts[index]);
        console.log("categorires", usedAmounts, leftAmounts);

        const resultObject = {
          usedAmounts,
          categoryName,
          amountsSums,
          leftAmounts,
        };
        const consolidatedBudgetData = {};

        data.budgets &&
          data.budgets.forEach((item) => {
            const {
              categoryId,
              categoryName,
              subCategoryId,
              usedAmount,
              leftAmount,
              totalAmount,
              image,
            } = item && item;
            const key = `${categoryName}_${categoryId}`;

            if (consolidatedBudgetData[key]) {
              consolidatedBudgetData[key].value.usedAmount += usedAmount;
              consolidatedBudgetData[key].value.leftAmount += leftAmount;
              consolidatedBudgetData[key].value.totalAmount += totalAmount;
            } else {
              consolidatedBudgetData[key] = {
                value: {
                  categoryId,
                  categoryName,
                  subCategoryId,
                  usedAmount,
                  leftAmount,
                  totalAmount,
                  image,
                },
              };
            }
          });
        const consolidatedBudgetArray = Object.values(consolidatedBudgetData);
        setBudgetData(resultObject);
        setCategoryBudget(consolidatedBudgetArray);
      }
      if (data && data.status === 200 && data.total) {
        console.log("data", data.total);
        setTotalBudget(data.total);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const getAllBudgets = async () => {
    try {
      setLoading(true);
      const date = moment();
      const startDate = date.startOf("month").format("YYYY-MM-DD"); // Start of the month
      const endDate = date.endOf("month").format("YYYY-MM-DD");
      let data = await BUDGET_API.fetchAllBudget(startDate, endDate, "all","false");
      if (data && data.status === 200 && data.categorized) {
        // setBudgetData(data.categorized);
        const promises =
          data.categorized &&
          data.categorized.map((budget) => {
            const payload = {
              startDate,
              endDate,
              category: budget.categoryId, // Send categoryId for this budget
              subCategory: budget.subCategoryId && budget.subCategoryId,
            };
            return BUDGET_API.getCalculatedBudgetTransactions(payload).then(
              (response) => ({
                budget, // Include the original budget
                response, // Include the calculated response
              })
            );
          });

        const results = await Promise.all(promises);
        const chartData = {
          usedAmounts: [],
          categoryName: [],
          amountsSums: [],
          leftAmounts: [],
        };
        const totalBudgetObj = {
          totalBudget: 0,
          totalTransactions: 0,
        };
        // Transform results into the desired structure
        const structuredData = results.map(({ budget, response }) => {
          const usedAmount = response.currentMonthTransactions.totalAmount || 0;
          const totalAmount = budget.amount || 0;
          const leftAmount = totalAmount - usedAmount;

          chartData.usedAmounts.push(usedAmount);
          chartData.categoryName.push(
            (budget.budgetCategory && budget.budgetCategory.categoryName) || ""
          );
          chartData.amountsSums.push(totalAmount);
          chartData.leftAmounts.push(leftAmount);

          totalBudgetObj.totalBudget += totalAmount;
          totalBudgetObj.totalTransactions += usedAmount;

          return {
            categoryId: budget && budget.categoryId && budget.categoryId,
            categoryName:
              (budget.budgetCategory && budget.budgetCategory.categoryName) ||
              "",
            subCategoryName: (
              (budget.budgetCategory &&
                budget.budgetCategory.subCategories &&
                budget.budgetCategory.subCategories.find(
                  (sub) => sub.id === budget.subCategoryId
                )) ||
              {}
            ).name,
            subCategoryId:
              (budget && budget.subCategoryId && budget.subCategoryId) || null,
            usedAmount,
            leftAmount,
            totalAmount,
            image: (budget.budgetCategory && budget.budgetCategory.image) || "", // Assuming `image` exists in `budgetCategory`
          };
        });
        // Log or set the structured data in state
        console.log("Structured Data:", structuredData, chartData);

        setTotalBudget(totalBudgetObj);
        if (chartData) {
          setBudgetData(chartData);
        }
        if (structuredData) {
          setCategoryBudget(structuredData);
        }
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  const getAllBudgetsMonthsCategory = async () => {
    try {
      setLoading(true);
      const date = moment();
      const startDate = date.startOf("month").format("YYYY-MM-DD"); // Start of the month
      const endDate = date.endOf("month").format("YYYY-MM-DD");
      let data = await BUDGET_API.fetchAllBudget(startDate, endDate, "month","false");
      if (data && data.status === 200 && data.categorized) {
        setMonthsBudgetData(data.categorized);
      }
      // await getCalculatedBudget();
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const getAll = async () => {
    await getAllBudgets();
    await getAllBudgetsMonthsCategory();
  };

  useEffect(() => {
    getAll();
  }, []);

  const totalUncategorizedAmount =
    monthsBudgetData &&
    monthsBudgetData.reduce((sum, item) => sum + item.amount, 0);
  const totalBudgetValue =
    totalUncategorizedAmount +
    (totalBudget && totalBudget.totalBudget ? totalBudget.totalBudget : 0);
  const totalBudgetValue1 = totalUncategorizedAmount
    ? totalUncategorizedAmount
    : 0;

  const data2 = {
    labels: budgetData && budgetData.categoryName && budgetData.categoryName,
    previousDate: {
      dataSet: budgetData && budgetData.usedAmounts && budgetData.usedAmounts,
    },
    currentDate: {
      dataSet: budgetData && budgetData.amountsSums && budgetData.amountsSums,
    },
  };
  const totalBudgets =
    totalBudget && totalBudget.totalBudget ? totalBudget.totalBudget : 0;
  const usedAmounts =
    budgetData &&
    budgetData.usedAmounts &&
    budgetData.usedAmounts.reduce((acc, curr) => acc + curr, 0);
  const percentage =
    totalBudgetValue1 > 0
      ? (((usedAmounts || 0) / totalBudgetValue1) * 100).toFixed(2)
      : "0.00";
  const dataDonught = {
    labels: budgetData && budgetData.categoryName && budgetData.categoryName,
    datasets: [
      {
        label: "Budget",
        data: budgetData && budgetData.usedAmounts && budgetData.usedAmounts,
        // backgroundColor: Array.from(
        //   { length: budgetData && budgetData.categoryName.length },
        //   () => "#" + Math.floor(Math.random() * 16777215).toString(16)
        // ),
        // borderColor: Array.from(
        //   { length: budgetData && budgetData.categoryName.length },
        //   () => "#" + Math.floor(Math.random() * 16777215).toString(16)
        // ),
        backgroundColor:
          budgetData &&
          budgetData.categoryName.map(
            (_, index) => ChartColors[index % ChartColors.length] // Cycle through ChartColors for backgroundColor
          ),
        borderColor:
          budgetData &&
          budgetData.categoryName.map(
            (_, index) => ChartColors[index % ChartColors.length] // Cycle through ChartColors for borderColor
          ),
        borderWidth: 0,
      },
    ],
    text: percentage && isNaN(percentage) ? "0%" : `${percentage}%`,
  };
  const centerTextPlugin = {
    id: "centerText",
    afterDraw: (chart) => {
      const { ctx, width, height } = chart;
      var text = chart.config.data.text;
      var sidePadding = 20;
      var sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);
      ctx.font = "bold 40px " + "Arial";
      //Get the width of the string and also the width of the element minus 10 to give it 5px side padding
      var stringWidth = ctx.measureText(text).width;
      var elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;
      // Find out how much the font can grow in width.
      var widthRatio = elementWidth / stringWidth;
      var newFontSize = Math.floor(30 * widthRatio);
      var elementHeight = chart.innerRadius * 2;

      // Pick a new font size so it will not be larger than the height of label.
      var fontSizeToUse = Math.min(newFontSize, elementHeight);

      var centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      var centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

      var fontSize = (height / 114).toFixed(2);
      ctx.font = fontSize + "em Verdana";
      ctx.textBaseline = "middle";

      ctx.textAlign = "center";
      ctx.font = fontSizeToUse + 3 + "px " + "Arial";

      var textX = Math.round((width - ctx.measureText(text).width) / 2);
      var textY = height / 2;

      ctx.fillText(text, centerX, centerY);
    },
  };

  const isComponentLoading = isLoading || loading;

  return (
    <React.Fragment>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "30%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div className="reportCard1" style={{ width: "100%" }}>
          <div className="budget-home-icon-container">
            <div
              className="asset-home-icon"
              style={{ textAlign: "center", width: "50%" }}
            >
              <img
                style={{ width: "100%", minWidth: "100px" }}
                src={budgetHomeIcon}
                alt="budget"
              />
            </div>
            <div style={{ textAlign: "center", width: "50%" }}>
              <h3
                className="Reportheading"
                style={{ fontSize: "16px", fontWeight: "800" }}
              >
                {moment().format("MM/DD/YYYY")}
              </h3>
              <h3
                className="Reportheading"
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  marginTop: "1rem",
                }}
              >
                Monthly Budget
              </h3>
              <h3 className="Reportheading" style={{ fontWeight: "900" }}>
                $
                {totalBudgetValue1
                  ? totalBudgetValue1
                      .toFixed(2)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : (0).toFixed(2)}
              </h3>
              <h3
                className="Reportheading"
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  marginTop: "1rem",
                }}
              >
                Spent
              </h3>
              <h3 className="Reportheading" style={{ fontWeight: "900" }}>
                ${" "}
                {budgetData && budgetData.usedAmounts
                  ? budgetData.usedAmounts
                      .reduce((acc, curr) => acc + curr, 0)
                      .toFixed(2)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : (0).toFixed(2)}
              </h3>
            </div>
          </div>

          <div>
            {isComponentLoading ? (
              <React.Fragment>
                <BudgetSummarySkeleton />
              </React.Fragment>
            ) : (
              budgetData &&
              budgetData.categoryName.length > 0 && (
                <Doughnut
                  pointStyle="circle"
                  data={dataDonught}
                  options={{
                    cutoutPercentage: 70,
                    responsive: true,
                    maintainAspectRatio: true,
                    legend: {
                      display:
                        budgetData && budgetData.categoryName.length < 10
                          ? true
                          : false,
                      position:
                        budgetData && budgetData.categoryName.length < 5
                          ? "left"
                          : "top",
                      labels: {
                        fontColor: "white",
                      },
                    },
                    tooltips: {
                      callbacks: {
                        label: function (tooltipItem, data) {
                          const label =
                            data.labels[tooltipItem.index] || "";
                          const value =
                            data.datasets[tooltipItem.datasetIndex].data[
                              tooltipItem.index
                            ] || 0;
                          return `${label}: $${value
                            .toFixed(2)
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
                        },
                      },
                    },
                  }}
                  plugins={[centerTextPlugin]}
                />
              )
            )}
          </div>
        </div>
        <BudgetTransaction
          budgetTransactions={transactions && transactions}
          clientBanks={clientBanks && clientBanks}
          isLoading={isComponentLoading}
        />
      </div>
      <div className="reportCard2" style={{ position: "relative" }}>
        <h1 className="liablitycardheading">
          Budget $
          {totalBudget && totalBudget.totalBudget
            ? totalBudget.totalBudget
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : (0).toFixed(2)}{" "}
          / $
          {totalBudget && totalBudget.totalTransactions
            ? totalBudget.totalTransactions
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : (0).toFixed(2)}
        </h1>
        <div
          style={{
            marginTop: "1rem",
            maxHeight: "calc(100vh - 300px)",
            overflow: "auto",
            paddingInline: "1rem",
          }}
        >
          {isComponentLoading ? (
            <React.Fragment>
              <BudgetListSkeleton rows={4} />
            </React.Fragment>
          ) : (
            categoryBudget &&
            categoryBudget.map((item, index) => {
              return (
                <div
                  key={index}
                  className=""
                  style={{ display: "flex", gap: "1rem", marginTop: "8px" }}
                >
                  <img
                    src={item.image ? item.image : budgetIcon}
                    width={80}
                    height={80}
                  ></img>
                  <div style={{ width: "100%" }}>
                    <div className="budgetDropDown">
                      <span className="budgetCardTitle">
                        {item && item.categoryName}{" "}
                        {item.subCategoryName &&
                          "(" + item.subCategoryName + ")"}
                      </span>
                      <span>
                        $
                        {item.leftAmount !== undefined
                          ? `${Math.abs(item.leftAmount)
                              .toFixed(2)
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                          : "$0"}{" "}
                        {(item.usedAmount ? item.usedAmount : 0) >
                        (item.totalAmount ? item.totalAmount : 0)
                          ? "Over"
                          : "Left"}
                      </span>
                    </div>
                    <Progress
                      className="budgetProgress"
                      type="line"
                      height="40px"
                      strokeColor={
                        item && item.usedAmount
                          ? item.usedAmount >
                            (item.totalAmount ? item.totalAmount : 0)
                            ? "#f5222d"
                            : "#2a3da3"
                          : "#2a3da3"
                      }
                      trailColor="#333533"
                      percent={
                        item && item.usedAmount
                          ? (item.usedAmount /
                              (item.totalAmount ? item.totalAmount : 0)) *
                            100
                          : 0
                      }
                      showInfo={false}
                    />
                    <div className="budgetDropDown">
                      <span className="budgetCardTitle">
                        $
                        {item.usedAmount &&
                          item.usedAmount
                            .toFixed(2)
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
                        of $
                        {(item.totalAmount ? item.totalAmount : 0)
                          .toFixed(2)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
